import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  Phone, 
  ArrowRight,
  Stethoscope,
  Heart,
  ShoppingBag,
  FileText,
  Package,
  Users
} from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Stethoscope: Stethoscope,
  Heart: Heart,
  ShoppingBag: ShoppingBag,
  FileText: FileText,
  Package: Package,
  Users: Users
};

const Services = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section 
        className="py-20"
        style={{ background: 'var(--gradient-hero-warm)' }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <motion.h1
              className="heading-hero mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Our Services
            </motion.h1>
            <motion.p
              className="body-large mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              From medical appointments to daily errands — we provide personal, 
              human assistance for seniors and peace of mind for families.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-[#FFF9F2]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Heart;
              return (
                <motion.div
                  key={service.id}
                  className={`service-card ${service.accent} p-8`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-full bg-white/60 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-7 h-7 text-[#232323]" />
                    </div>
                    <div>
                      <h3 className="heading-2 mb-3">{service.title}</h3>
                      <p className="body-medium text-[#353535] leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Custom Services Note */}
          <motion.div
            className="mt-12 bg-white rounded-2xl p-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="heading-2 mb-4">Need Something Else?</h3>
            <p className="body-large max-w-2xl mx-auto mb-6">
              Don't see what you're looking for? We're flexible. Reach out and tell us 
              what help you need — if we can do it, we will.
            </p>
            <Link to="/request-help" className="btn-primary gap-2 inline-flex">
              <Phone className="w-4 h-4" />
              Tell Us What You Need
            </Link>
          </motion.div>
        </div>
      </section>

      {/* For Adult Children Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-sm text-[#666] uppercase tracking-wide mb-4 block">
                For Adult Children
              </span>
              <h2 className="heading-1 mb-6">
                Managing Care from Afar?
              </h2>
              <p className="body-large mb-6">
                Many of our clients are adult children living in different cities 
                or countries. We understand the challenges of caring for aging parents remotely.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Request services on behalf of your parents',
                  'Receive regular updates after every visit',
                  'Coordinate care without being physically present',
                  'Get peace of mind knowing someone trustworthy is there'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#4caf50] mt-2 flex-shrink-0" />
                    <span className="text-[#353535]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/request-help" className="btn-primary gap-2">
                Request Help for Your Parent
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              className="bg-[#FFF9F2] rounded-2xl p-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <blockquote className="text-lg text-[#353535] italic leading-relaxed mb-6">
                "Living abroad, I was always worried about my mother. Now I know 
                there's someone I can call whenever she needs help. They keep me 
                updated after every visit. It's such a relief."
              </blockquote>
              <div>
                <p className="font-medium text-[#232323]">Priya S.</p>
                <p className="text-sm text-[#666]">Daughter, lives in Singapore</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;